import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Search, UserCheck, UserX, User, Phone, Hash, AlertCircle, CheckCircle2, RefreshCw } from 'lucide-react';
import { bousanmSolideApi } from '../services/bousanmSolideApi';
import Swal from 'sweetalert2';

const AssignationsManagement = () => {
  const [compteCode, setCompteCode] = useState('');
  const [compteInfo, setCompteInfo] = useState(null);
  const [searchPerformed, setSearchPerformed] = useState(false);
  const [loading, setLoading] = useState(false);
  
  // États pour modale assignation
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [newAgentUsername, setNewAgentUsername] = useState('');
  const [assignReason, setAssignReason] = useState('Nouvelle assignation');
  const [isAssigning, setIsAssigning] = useState(false);

  const handleSearch = async () => {
    if (!compteCode.trim()) return;
    
    setLoading(true);
    setSearchPerformed(true);
    
    try {
      const response = await bousanmSolideApi.getCompteAgent(compteCode);
      setCompteInfo(response);
    } catch (err) {
      console.error(err);
      setCompteInfo(null);
    } finally {
      setLoading(false);
    }
  };

  const handleUnassign = async () => {
    try {
      const result = await Swal.fire({
        title: 'Êtes-vous sûr ?',
        text: "L'agent actuel sera retiré de ce compte.",
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Oui, désassigner',
        cancelButtonText: 'Annuler'
      });

      if (result.isConfirmed) {
        setLoading(true);
        await bousanmSolideApi.unassignAgent(compteInfo.data.code);
        
        await Swal.fire('Désassigné !', 'L\'agent a été retiré avec succès.', 'success');
        
        // Rafraichir les infos
        handleSearch();
      }
    } catch (error) {
      console.error("Erreur désassignation:", error);
      Swal.fire('Erreur', "Impossible de désassigner l'agent", 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleAssignSubmit = async (e) => {
    e.preventDefault();
    if (!newAgentUsername) return;

    setIsAssigning(true);
    try {
      await bousanmSolideApi.assignAgent({
        compte_code: compteInfo.data.code,
        agent_username: newAgentUsername,
        raison: assignReason
      });
      
      Swal.fire({
        icon: 'success',
        title: 'Succès',
        text: `Agent ${newAgentUsername} assigné avec succès`,
        timer: 2000,
        showConfirmButton: false
      });
      
      setAssignModalOpen(false);
      setNewAgentUsername('');
      
      // Rafraichir les infos
      handleSearch();
    } catch (error) {
      console.error("Erreur assignation:", error);
      Swal.fire({
        icon: 'error',
        title: 'Erreur',
        text: "Erreur lors de l'assignation de l'agent. Vérifiez le nom d'utilisateur."
      });
    } finally {
      setIsAssigning(false);
    }
  };

  return (
    <div className="h-full w-full bg-gray-50/50 overflow-y-auto p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gestion des Assignations</h1>
          <p className="text-sm text-gray-500">
            Recherchez et gérez les agents assignés aux comptes clients
          </p>
        </div>
      </div>

      {/* Search Card */}
      <Card className="border-none shadow-sm bg-white mb-6">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Search className="h-5 w-5 text-gray-500" />
            Rechercher un compte
          </CardTitle>
          <CardDescription>
            Entrez le code du compte pour voir son agent assigné
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2 max-w-md">
            <div className="relative flex-1">
              <Hash className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <Input
                placeholder="Code du compte (ex: 500524264)"
                value={compteCode}
                onChange={(e) => setCompteCode(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                className="pl-9 bg-gray-50"
              />
            </div>
            <Button onClick={handleSearch} disabled={loading} className="gap-2">
              {loading ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
              Rechercher
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Results */}
      {searchPerformed && (
        <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
          {compteInfo?.success ? (
            <div className="grid gap-6 md:grid-cols-2">
              {/* Client Info Card */}
              <Card className="border-none shadow-sm bg-white">
                <CardHeader className="pb-3 border-b border-gray-50">
                  <CardTitle className="text-base flex items-center gap-2">
                    <User className="h-5 w-5 text-blue-500" />
                    Informations du Client
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4 pt-6">
                  <div className="flex items-start justify-between">
                    <div className="space-y-4 flex-1">
                      <div>
                        <p className="text-xs text-gray-500 mb-1 font-medium uppercase tracking-wide">Nom complet</p>
                        <p className="text-lg font-bold text-gray-900">
                          {compteInfo.data.client.prenom} {compteInfo.data.client.nom}
                        </p>
                      </div>
                      
                      <div>
                        <p className="text-xs text-gray-500 mb-1 font-medium uppercase tracking-wide">Contact</p>
                        <div className="flex items-center gap-2">
                          <Phone className="h-4 w-4 text-gray-400" />
                          <p className="text-sm font-medium text-gray-700">
                            {compteInfo.data.client.tel}
                          </p>
                        </div>
                        <p className="text-xs text-gray-400 mt-1 ml-6">{compteInfo.data.client.email}</p>
                      </div>
                      
                      <div>
                        <p className="text-xs text-gray-500 mb-1 font-medium uppercase tracking-wide">Compte</p>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="font-mono bg-gray-50 text-gray-700 border-gray-200">
                            {compteInfo.data.code}
                          </Badge>
                          <Badge className="bg-blue-50 text-blue-700 border-blue-100 hover:bg-blue-50">
                            {compteInfo.data.plan}
                          </Badge>
                        </div>
                      </div>
                    </div>
                    
                    <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center text-white text-2xl font-bold shadow-lg shadow-blue-200">
                      {compteInfo.data.client.prenom.charAt(0)}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Agent Assignment Card */}
              <Card className="border-none shadow-sm bg-white">
                <CardHeader className="pb-3 border-b border-gray-50">
                  <CardTitle className="text-base flex items-center gap-2">
                    <UserCheck className="h-5 w-5 text-green-500" />
                    Agent Assigné
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-6">
                  {compteInfo.data.agent_assigne ? (
                    <div className="space-y-6">
                      <div className="flex items-center gap-4 p-4 bg-green-50/50 border border-green-100 rounded-xl">
                        <div className="w-12 h-12 bg-white border-2 border-green-100 rounded-full flex items-center justify-center text-green-600 font-bold text-lg shadow-sm">
                          {compteInfo.data.agent_assigne.username.charAt(0).toUpperCase()}
                        </div>
                        <div className="flex-1">
                          <p className="font-bold text-gray-900 text-lg">
                            @{compteInfo.data.agent_assigne.username}
                          </p>
                          <p className="text-sm text-gray-500">{compteInfo.data.agent_assigne.nom_complet}</p>
                          <div className="flex items-center gap-1.5 mt-1">
                            <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></div>
                            <p className="text-xs text-green-600 font-medium">
                              Agent actif
                            </p>
                          </div>
                        </div>
                      </div>
                      
                      <Button 
                        variant="outline" 
                        className="w-full gap-2 border-red-100 text-red-600 hover:text-red-700 hover:bg-red-50 hover:border-red-200"
                        onClick={handleUnassign}
                      >
                        <UserX className="h-4 w-4" />
                        Désassigner l'agent
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-6">
                      <div className="flex items-center gap-4 p-4 bg-orange-50/50 border border-orange-100 rounded-xl">
                        <div className="p-3 bg-white rounded-full border border-orange-100 shadow-sm">
                          <AlertCircle className="h-6 w-6 text-orange-500" />
                        </div>
                        <div>
                          <p className="text-base font-bold text-orange-900">
                            Aucun agent assigné
                          </p>
                          <p className="text-sm text-orange-600/80 mt-0.5">
                            Ce compte n'a pas d'agent attitré.
                          </p>
                        </div>
                      </div>
                      
                      <Button 
                        className="w-full gap-2 bg-blue-600 hover:bg-blue-700"
                        onClick={() => setAssignModalOpen(true)}
                      >
                        <UserCheck className="h-4 w-4" />
                        Assigner un agent
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          ) : (
            <Card className="border-red-100 bg-red-50/50 shadow-sm">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-red-100 rounded-full">
                    <UserX className="h-6 w-6 text-red-600" />
                  </div>
                  <div>
                    <p className="font-bold text-red-900 text-lg">Compte introuvable</p>
                    <p className="text-sm text-red-600/90 mt-1">
                      Aucun compte Bousanm Solidè trouvé avec le code <span className="font-mono font-bold bg-red-100 px-1.5 py-0.5 rounded border border-red-200">{compteCode}</span>.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Info Card */}
      {!searchPerformed && (
        <div className="mt-6 p-6 bg-blue-50/50 border border-blue-100 rounded-xl flex items-start gap-4">
          <div className="p-2 bg-blue-100 rounded-lg">
            <AlertCircle className="h-5 w-5 text-blue-600" />
          </div>
          <div className="space-y-2">
            <p className="font-bold text-blue-900">Comment gérer les assignations ?</p>
            <ul className="text-sm text-blue-700 space-y-1.5 list-disc list-inside">
              <li>Entrez le code unique du compte client dans le champ de recherche ci-dessus.</li>
              <li>Vérifiez l'identité du client et l'agent actuellement assigné.</li>
              <li>Vous pouvez <strong>désassigner</strong> l'agent actuel ou en <strong>assigner</strong> un nouveau si le compte est libre.</li>
            </ul>
          </div>
        </div>
      )}

      {/* Dialog Assignation Agent */}
      <Dialog open={assignModalOpen} onOpenChange={setAssignModalOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Assigner un agent</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAssignSubmit} className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="client" className="text-sm font-medium text-gray-500">Compte Client</Label>
              <div className="p-2 bg-gray-50 rounded border text-sm font-medium">
                {compteInfo?.data?.code} - {compteInfo?.data?.client?.prenom} {compteInfo?.data?.client?.nom}
              </div>
            </div>
            
            <div className="grid gap-2">
              <Label htmlFor="agent" className="text-right">Nom d'utilisateur de l'agent</Label>
              <Input
                id="agent"
                value={newAgentUsername}
                onChange={(e) => setNewAgentUsername(e.target.value)}
                placeholder="ex: ent4455"
                className="col-span-3"
                required
              />
              <p className="text-[10px] text-gray-500">Entrez le username exact de l'agent.</p>
            </div>
            
            <div className="grid gap-2">
              <Label htmlFor="raison" className="text-right">Raison</Label>
              <Input
                id="raison"
                value={assignReason}
                onChange={(e) => setAssignReason(e.target.value)}
                placeholder="Raison de l'assignation..."
                className="col-span-3"
              />
            </div>
            
            <DialogFooter className="mt-4">
              <Button type="button" variant="outline" onClick={() => setAssignModalOpen(false)} disabled={isAssigning}>
                Annuler
              </Button>
              <Button type="submit" disabled={isAssigning || !newAgentUsername}>
                {isAssigning ? 'Assignation...' : 'Confirmer'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AssignationsManagement;
